/**
 * Global open/close state for the Pre-Meeting Brief v2 workbench overlay.
 *
 * Every PMB v2 entry button across the app (priority page, person card,
 * marketing-os people/call-log, accounts people-list, knowledge-os pre-
 * meeting form, etc.) calls `usePmbV2WorkbenchStore.getState().open({...})`
 * instead of navigating to /os/agents. A single <PreMeetingBriefV2Workbench />
 * mounted at the root of app/root.tsx reads this store and renders the
 * portal-based overlay on top of whatever page the user is currently on —
 * no route change.
 *
 * The widget itself (sections + chat) is built from existing v2 pieces:
 * AgentSearchV2 + AgentIdMessagesV2 + AgentRightPanel inside a portal.
 * See containers/pre-meeting-brief-v2/pmb-v2-workbench.tsx.
 */
import { create } from "zustand"

export interface PmbV2WorkbenchInput {
  /** LinkedIn URL — the only hard requirement. The agent's pmb_person step
   *  resolves company from this when account/apollo aren't provided. */
  linkedinUrl: string
  /** Internal integer account id when known (preferred). */
  accountId?: number | string | null
  /** Apollo ObjectId (24-hex). Either accountId or apolloId is fine. */
  apolloId?: string | null
  /** Opportunity id (priority_id) when launched from a priority/opp context. */
  opportunityId?: number | string | null
  /** Free-form telemetry label — "priority_card", "marketing_os_people_list",
   *  "person_card", etc. */
  source?: string | null
  /** Header chrome — purely cosmetic. */
  personName?: string | null
  accountName?: string | null
}

interface PmbV2WorkbenchStore {
  isOpen: boolean
  input: PmbV2WorkbenchInput | null
  open: (input: PmbV2WorkbenchInput) => void
  close: () => void
}

const usePmbV2WorkbenchStore = create<PmbV2WorkbenchStore>((set) => ({
  isOpen: false,
  input: null,
  open: (input) => set({ isOpen: true, input }),
  close: () => set({ isOpen: false, input: null })
}))

export default usePmbV2WorkbenchStore
